import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { MapContainer, TileLayer, Polygon, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';

import PollWindow from './PollWindow/PollWindow';

import { getPollData, getRidingShapes, getPollsShapesByRidingId } from '../../../http/pollsAPI';
import { getWardsShapes, getWardData } from '../../../http/wardsAPI';

import './Map.scss';

export const FitBounds = ({ polygons, boundsFittedRef, setMapCenter, setMapZoom }) => {
  const map = useMap();

  useEffect(() => {
    if (boundsFittedRef && boundsFittedRef.current) return;
    if (!polygons || polygons.length === 0) return;

    const allCoords = polygons.flatMap((p) => p.coordinates);
    const bounds = L.latLngBounds(allCoords);
    map.fitBounds(bounds, { padding: [20, 20] });

    if (boundsFittedRef) {
      setTimeout(() => {
        setMapCenter(map.getCenter());
        setMapZoom(map.getZoom());
        boundsFittedRef.current = true;
      }, 300);
    }
  }, [polygons, map, boundsFittedRef, setMapCenter, setMapZoom]);

  return null;
};

const Map = ({ pollId, wardId, isCouncillor = false }) => {
  const config = useSelector((state) => state.config.config);

  const [polygons, setPolygons] = useState([]);
  const [activePolygon, setActivePolygon] = useState({ id: 0, coordinates: [], title: '' });

  const [ridingPoly, setRidingPoly] = useState([]);
  const [cp, setCp] = useState([]);
  const [mapData, setMapData] = useState(false);

  const [isPolygonWindowOpen, setIsPolygonWindowOpen] = useState(false);
  const [polygonDemographics, setPolygonDemographics] = useState(null);
  const [polygonMessages, setPolygonMessages] = useState(null);

  const { userInfo } = useOutletContext();

  const pollIdExists = pollId !== 'NaN';
  const wardIdExists = wardId !== 'NaN';

  useEffect(() => {
    const fetchOrLoadData = async () => {
      if (!config) {
        setTimeout(fetchOrLoadData, 1000);
        return;
      }
      try {
        if (isCouncillor) {
          const res = await getWardsShapes(config, userInfo.city);

          setPolygons(res.data.rows.wards);
          // setRidingPoly(res.data.rows.wards[0].coordinates);

          if (wardIdExists) {
            setActivePolygon(res.data.rows.wards.find((w) => w.id === wardId));
          }
        } else {
          const res = pollIdExists
            ? await getRidingShapes(config, pollId)
            : await getPollsShapesByRidingId(config, userInfo.riding);

          setPolygons(pollIdExists ? res.data.rows.riding_polls : res.data.rows.polls);
          setCp(res.data.rows.riding[0].cp);
          setRidingPoly(res.data.rows.riding[0].coordinates);

          if (pollIdExists) {
            setActivePolygon(res.data.rows.poll[0]);
          }
        }

        setMapData(true);
      } catch (err) {
        console.error('Error fetching or loading polygons:', err);
      }
    };

    fetchOrLoadData();
  }, [config, pollId, wardId]);

  const handlePolygonClick = async (polygonId) => {
    try {
      setIsPolygonWindowOpen(true);

      const data = isCouncillor
        ? await getWardData(config, userInfo.id, polygonId)
        : await getPollData(config, userInfo.id, polygonId);

      setPolygonDemographics(data.data.demographics ?? []);
      setPolygonMessages(data.data.messages ?? []);
    } catch (err) {
      console.error(`Failed to load polygon data ${polygonId}:`, err);
    }
  };

  const closePolygonWindow = () => {
    setIsPolygonWindowOpen(false);
    setPolygonDemographics(null);
    setPolygonMessages(null);
  };

  if (!mapData) {
    return null;
  }

  return (
    <MapContainer style={{ width: '100%', height: '100%' }} zoom={13} center={cp.length > 0 ? cp : [0, 0]}>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      {ridingPoly.length > 0 ? <Polygon positions={ridingPoly} pathOptions={{ color: 'red' }} /> : ''}
      {polygons.map((polygon) => (
        <Polygon
          key={polygon.id}
          positions={polygon.coordinates}
          pathOptions={polygon.id === activePolygon.id ? {} : { color: 'rgba(0,0,0,0.23)' }}
          eventHandlers={{
            click: () => handlePolygonClick(polygon.id),
          }}
        >
          <Tooltip sticky>{polygon.title}</Tooltip>
        </Polygon>
      ))}
      <FitBounds polygons={polygons} />
      {isPolygonWindowOpen && (
        <PollWindow
          messages={polygonMessages}
          data={polygonDemographics}
          isOpen={isPolygonWindowOpen}
          onCloseHandler={closePolygonWindow}
          userInfo={userInfo}
        />
      )}
    </MapContainer>
  );
};

export default Map;
